/**
 * Funnels — every funnel in the workspace, its status, and what it produced.
 *
 * Visual Reference: `design/screens/funnels-list.html`, four states — default,
 * loading, empty, error.
 *
 * PRD §7.4: "**Funnels list** | All funnels in the workspace, status,
 * submission counts | Owner, Admin, Editor | M1".
 *
 * THE REVIEW BANNER ARRIVED WITH THE SCAN THAT POPULATES IT — Story 25.4, the
 * `moderation` state. Until then DECISION-016 kept the mockup's amber "Waiting
 * for review before it can publish" off the card, because a review banner that
 * nothing can ever clear reads worse than none. Its "See what reviewers check"
 * link still waits: what reviewers check is PRD §7.7's operator criteria, M6,
 * and nothing states them yet (BACKLOG, 2026-09-28).
 *
 * Stories 6.1 and 6.8 — 6.1 drew the overflow menu with one of its four items
 * wired, and 6.8 wired the other three to the services 6.6 and 6.7 had already
 * built. DECISION-063 required all four before go-live.
 */
import { useCallback, useEffect, useState } from 'react'
import { apiGet, apiPost, ApiFailure } from '../lib/api'
import { relative } from '../lib/relative'
import { useShell } from '../shell/workspace'
import { Button } from '../ui/Button'
import { Menu } from '../shell/Menu'
import { LockMark } from '../ui/LockMark'
import { Modal } from '../ui/Modal'
import { Pill } from '../ui/Pill'
import { Skeleton } from '../ui/Skeleton'
import { COMPOSED, DRAWN, type Intent, type Vertical } from '../editor/publish-copy'
import { DRAWN as PROPOSAL } from '../editor/proposal-copy'
import { NewFunnelDialog } from './NewFunnel'

/**
 * The ⋯ control, as `funnels-list.html` draws it: 34px square, radius 10px,
 * white on the card with the ground tint on hover. `settings-members.html`
 * draws the same control at radius 8px, which is why `Menu` takes the class
 * rather than owning one.
 */
const TRIGGER = [
  'flex size-mdx-icon-button shrink-0 cursor-pointer items-center justify-center',
  'rounded-mdx-control border border-mdx-line bg-mdx-white text-mdx-15 font-mdx-bold',
  'text-mdx-ink transition-colors ease-mdx-swift hover:bg-mdx-ground',
].join(' ')

/** The three tone buckets, in the bar's own order — RULE-002's grey→amber→green
 *  read the other way round, because the bar leads with the best. */
const TONES = ['hot', 'review', 'nurture'] as const
type Tone = (typeof TONES)[number]

const FILL: Record<Tone, string> = {
  hot: 'bg-mdx-hot',
  review: 'bg-mdx-review',
  nurture: 'bg-mdx-nurture',
}

type FunnelCard = {
  id: string
  name: string
  archetype: string
  status: 'draft' | 'published' | 'unpublished' | 'archived'
  submissions30d: number
  bands: Record<Tone, number>
  /** Counted, not banded — DECISION-061. The locked segment's number. */
  overCap: number
  updatedAt: number
  updatedByName: string | null
  /**
   * Held for review, or blocked on this plan (Story 25.4), from the funnel's one
   * moderation row. Optional because a list served by an API older than 25.4
   * carries no such field, and absent reads as nothing to say.
   */
  moderation?:
    | { state: 'held'; intents: Intent[]; at: number }
    | { state: 'blocked'; verticals: Vertical[]; at: number }
    | null
}

type View = { funnels: FunnelCard[]; limit: number; plan: string }

/** The chip, per status. `archived` never reaches the list — the query excludes
 *  it — but the map is total so a future status cannot render as nothing. */
const STATUS: Record<FunnelCard['status'], { label: string; tone: 'hot' | 'neutral' | 'review' }> = {
  published: { label: 'Published', tone: 'hot' },
  draft: { label: 'Draft', tone: 'neutral' },
  unpublished: { label: 'Unpublished', tone: 'review' },
  archived: { label: 'Archived', tone: 'neutral' },
}

/** PRD §3.1's five archetypes, in the operator's words rather than the enum's. */
const ARCHETYPE: Record<string, string> = {
  scorecard: 'Scorecard',
  qualifier: 'Qualifier',
  recommender: 'Recommender',
  triage: 'Triage',
  eligibility: 'Eligibility',
}

export function Funnels() {
  const { workspace, org } = useShell()
  const workspaceId = workspace?.id ?? null

  const [view, setView] = useState<View | null>(null)
  const [error, setError] = useState<string | null>(null)
  /**
   * The ACTION channel, and it is not the one above.
   *
   * `error` replaces the whole list with the "Couldn't load your funnels" card,
   * which is right for a failed GET and wrong for everything else. A duplicate
   * refused past the plan's funnel cap would have blanked four healthy cards
   * and re-titled a 409 as a load failure. `settings-workspaces.html` draws the
   * other shape — an alert above the grid, the grid intact — and Story 4.2
   * built it. Story 7.5's Save as blueprint wrote to `error`; it writes here.
   */
  const [actionError, setActionError] = useState<string | null>(null)
  /** The funnel whose archive confirmation is open. Null means none is. */
  const [archiving, setArchiving] = useState<FunnelCard | null>(null)
  /**
   * The funnel whose unpublish confirmation is open — its own state rather than
   * a mode on `archiving`, because the two modals say opposite things about
   * whether there is a way back and one flag would eventually get read wrong.
   */
  const [unpublishing, setUnpublishing] = useState<FunnelCard | null>(null)
  /** The funnel an action is in flight for — it holds the modal's button. */
  const [busy, setBusy] = useState<string | null>(null)
  /** Story 27.4's dialog, and which contents it opens on — null while closed. */
  const [starting, setStarting] = useState<'choose' | 'describe' | null>(null)

  /**
   * `quiet` keeps the cards on screen while a re-read runs.
   *
   * The mount blanks to the skeleton because there is nothing yet to preserve.
   * A refresh AFTER an action has four real cards on screen, and dropping them
   * to skeletons to show a fifth reads as the page breaking rather than as the
   * thing having worked.
   */
  const load = useCallback(
    async (quiet = false) => {
      if (!workspaceId) return
      setError(null)
      if (!quiet) setView(null)
      try {
        setView(await apiGet<View>(`/v1/w/${workspaceId}/funnels`))
      } catch (e) {
        // The message NAMES THE FIX and is rendered as it arrives (ARCH §4.3).
        // The mockup's own error copy is the fallback, not an override.
        setError(
          e instanceof ApiFailure
            ? e.message
            : 'The request came back empty-handed. Your funnels are still live and still collecting — load the list again.',
        )
      }
    },
    [workspaceId],
  )

  useEffect(() => {
    void load()
  }, [load])

  /**
   * One shape for the writes that stay on this page.
   *
   * Clear the last complaint, hold the card, run it, re-read quietly. The
   * server's own message is rendered as it arrives (ARCH §4.3) — including the
   * 409 `duplicateFunnel` throws at the plan's funnel cap, which already names
   * its fix. The fallback sentence is reached only when there is no
   * `ApiFailure` to quote, which means the request never landed.
   */
  const act = async (funnelId: string, run: () => Promise<unknown>, fallback: string) => {
    setActionError(null)
    setBusy(funnelId)
    try {
      await run()
      await load(true)
      return true
    } catch (e) {
      setActionError(e instanceof ApiFailure ? e.message : fallback)
      return false
    } finally {
      setBusy(null)
    }
  }

  /**
   * Story 6.6's `duplicateFunnel`, which names the copy `{name} copy` and hands
   * back its id.
   *
   * IT DOES NOT NAVIGATE, and Save as blueprint below does. The difference is
   * where the new thing lives: a blueprint lands in the library, so showing
   * somebody the shelf is the honest feedback; a copy lands in THIS list, so
   * the honest feedback is the list showing it. `load(true)` is what re-reads
   * it — the copy arrives with the real card's shape rather than one assembled
   * here from a two-field response.
   */
  const duplicate = async (funnel: FunnelCard) => {
    const wid = workspaceId
    if (!wid) return
    await act(
      funnel.id,
      () => apiPost(`/v1/w/${wid}/funnels/${funnel.id}/duplicate`, {}),
      "We couldn't duplicate that funnel. Check your connection and try again.",
    )
  }

  /**
   * Story 6.7's surface, rather than a second copy of it.
   *
   * The snippet, the two display modes, the domain allowlist and "email it to a
   * developer" are all the Connect tab's Embed card — its FIRST card, so the
   * tab opens on it. Rebuilding any of that here would be a second place for
   * the plan's embed host to be wrong (RULE-022), and the host is a thing the
   * server decides.
   */
  const openEmbed = (funnel: FunnelCard) => {
    window.location.assign(`/funnels/${funnel.id}/connect`)
  }

  /**
   * Story 6.6's `unpublishFunnel`, once the modal below has been answered.
   *
   * THE LIGHT STOP. Archive was the only drawn way to stop a published funnel
   * answering and archive is irreversible — `services/funnels/publish.ts` says
   * there is no unarchive route at MVP — so the product offered the heavy
   * permanent action and not the reversible one (DECISION-122, Q-087).
   *
   * It goes through `act()` like the others, which is what makes the status
   * chip reach `Unpublished` with no manual reload: `load(true)` re-reads the
   * list quietly and the card comes back with the server's own status rather
   * than one patched in here.
   */
  const unpublish = async () => {
    const wid = workspaceId
    if (!wid || !unpublishing) return
    const done = await act(
      unpublishing.id,
      () => apiPost(`/v1/w/${wid}/funnels/${unpublishing.id}/unpublish`, {}),
      "We couldn't unpublish that funnel. Check your connection and try again.",
    )
    // The modal stays open on a refusal, holding the API's own message —
    // `ArchiveModal`'s pattern and 16.1's `PublishModal`'s.
    if (done) setUnpublishing(null)
  }

  /** Story 6.6's `archiveFunnel`, once the modal below has been answered. */
  const archive = async () => {
    const wid = workspaceId
    if (!wid || !archiving) return
    const done = await act(
      archiving.id,
      () => apiPost(`/v1/w/${wid}/funnels/${archiving.id}/archive`, {}),
      "We couldn't archive that funnel. Check your connection and try again.",
    )
    // The modal stays open on a refusal, holding its own message. The list
    // behind it has already dropped the card if the archive did land.
    if (done) setArchiving(null)
  }

  /**
   * Story 7.5. The server strips the styling — PRD §3.3's "'save as blueprint'
   * strips styling automatically" is a property of the write, not of this call.
   *
   * IT LANDS THE OPERATOR IN THE LIBRARY, because that is where the thing they
   * just made now is. The export draws no confirmation toast and inventing one
   * would be a value from outside the mockups; showing them the shelf is the
   * honest feedback that needs no new surface.
   */
  const saveAsBlueprint = async (funnel: FunnelCard) => {
    if (!workspaceId) return
    setActionError(null)
    try {
      await apiPost(`/v1/w/${workspaceId}/funnels/${funnel.id}/save-as-blueprint`, {
        name: funnel.name,
      })
      window.location.assign('/library')
    } catch (e) {
      setActionError(
        e instanceof ApiFailure
          ? e.message
          : "We couldn't save that as a blueprint. Check your connection and try again.",
      )
    }
  }

  return (
    <div className="flex flex-col gap-mdx-24">
      <header className="flex flex-wrap items-end justify-between gap-mdx-16">
        <div className="flex flex-col gap-mdx-4">
          <span className="text-mdx-12 font-mdx-bold uppercase tracking-mdx-eyebrow text-mdx-slate">
            {org?.name ?? workspace?.name ?? ''}
          </span>
          <h1 className="m-0 text-mdx-25 font-mdx-bold leading-mdx-heading tracking-mdx-h1">
            Funnels{' '}
            <span className="font-mdx-medium text-mdx-slate tabular-nums">
              {view ? view.funnels.length : ''}
            </span>
          </h1>
        </div>
        {/*
          IT GOES TO THE LIBRARY, because that is the only create path that
          exists — and it was disabled for a reason that stopped being true.

          The comment here read "Story 6.2 is the editor this opens. Until it
          exists the button would have nowhere to go", citing Q-040. Story 6.2
          shipped 2026-08-26, `/funnels/:fid` has routed to the editor ever
          since, and Q-040 is answered and struck through — so this button spent
          eleven days as the primary call to action on a page where a new
          account lands, disabled, with nothing else on the screen that creates
          a funnel. DECISION-063 anticipated exactly this and said 6.2 "only has
          to remove the attribute and navigate". Nobody did.

          IT DOES NOT CREATE A BLANK FUNNEL, and that is deliberate rather than
          lazy. `POST /v1/w/{wid}/funnels` requires an `archetype`, the closed
          five-value enum that decides outcome semantics and Verdict sentences
          (PRD §3.2) — and NOTHING in the dashboard draws a picker for it, nor
          can it be changed afterwards: `archetype` appears in no file under
          `editor/`. Defaulting it here would silently lock a new account into
          semantics it never chose, with no way back. That was Q-084, and
          DECISION-119 answered it on 2026-09-08: a blank funnel is NOT reachable
          at MVP, so this navigation is now the permanent behaviour rather than
          the conservative one.

          A blueprint carries its own archetype ("the blueprint's own archetype
          wins over anything sent"), so the library path needs no such choice —
          which is why it is the one that works today.
          fixes: reported from a real signup — a new account could not create a
          funnel at all, 2026-09-08

          SINCE M3 IT OPENS A CHOICE (Story 27.4): the templates, as before, or a
          description drafted into a proposal. The second way in keeps DECISION-119
          whole — a proposal carries its archetype, shown before anything exists, and
          accepting is what creates the funnel (DECISION-167), so no blank funnel is
          reachable from here either.
        */}
        <Button onClick={() => setStarting('choose')}>New funnel</Button>
      </header>

      {/*
        The action channel. While EITHER confirmation is open its own copy
        carries the message instead, so one refusal never prints twice on one
        screen.

        Story 16.3 added the second modal and this guard had to grow with it —
        `!archiving` alone printed a refused Unpublish in both places at once.
        Found by the test written for that refusal, which is the only reason it
        was not shipped: nothing else looks at two error surfaces together.
      */}
      {actionError && !archiving && !unpublishing ? (
        <p role="alert" className="m-0 text-mdx-15 text-mdx-error">
          {actionError}
        </p>
      ) : null}

      {error ? (
        <StateCard
          tone="error"
          title="Couldn't load your funnels"
          body={error}
          action={<Button onClick={() => void load()}>Try again</Button>}
        />
      ) : !view ? (
        <LoadingGrid />
      ) : view.funnels.length === 0 ? (
        <StateCard
          tone="empty"
          title="No funnels yet"
          body={PROPOSAL.emptyBody}
          action={
            /*
              ONE action, because every funnel starts from a template.

              The template path WORKS and is the one a new account needs: Story
              7.4's library, live at `/library`, and production carries the
              three curated blueprints every account starts from
              (`presets.org_id IS NULL`) — checked against the database, not
              assumed, because routing to an empty page would unblock nobody.

              `Start from scratch` stood here drawn and disabled under
              DECISION-063's house rule. DECISION-119 (Q-084, raised by a real
              signup on production) settled that a blank funnel is NOT reachable
              at MVP: the archetype is PRD §3.2's closed enum, nothing in the
              dashboard collects it and nothing can correct it afterwards, so
              the control had no honest wiring — it is REMOVED rather than
              wired. The mockup lost it first; `funnels-list.html` was
              re-ingested 2026-09-08, and the second sentence of the body went
              with it, because "building from scratch takes an afternoon"
              described a path that no longer exists.

              AND A SECOND WAY IN SINCE M3 (Story 27.4): the drawn "Describe your
              business", secondary, straight to the describe dialog — a
              description becomes a proposal the person checks before any funnel
              exists, which is DECISION-119's line kept rather than crossed.
            */
            <>
              <Button onClick={() => window.location.assign('/library')}>{PROPOSAL.fromTemplate}</Button>
              <Button variant="secondary" onClick={() => setStarting('describe')}>
                {PROPOSAL.describe}
              </Button>
            </>
          }
        />
      ) : (
        <Grid>
          {view.funnels.map((funnel) => (
            <Card
              key={funnel.id}
              funnel={funnel}
              onDuplicate={duplicate}
              onSaveAsBlueprint={saveAsBlueprint}
              onGetEmbedCode={openEmbed}
              onUnpublish={setUnpublishing}
              onArchive={setArchiving}
            />
          ))}
        </Grid>
      )}

      {unpublishing ? (
        <UnpublishModal
          funnel={unpublishing}
          busy={busy === unpublishing.id}
          error={actionError}
          onClose={() => {
            setUnpublishing(null)
            setActionError(null)
          }}
          onConfirm={() => void unpublish()}
        />
      ) : null}

      {archiving ? (
        <ArchiveModal
          funnel={archiving}
          busy={busy === archiving.id}
          error={actionError}
          onClose={() => {
            setArchiving(null)
            setActionError(null)
          }}
          onConfirm={() => void archive()}
        />
      ) : null}

      {starting !== null && workspaceId ? (
        <NewFunnelDialog
          workspaceId={workspaceId}
          org={org?.name ?? workspace?.name ?? ''}
          opening={starting}
          onClose={() => setStarting(null)}
        />
      ) : null}
    </div>
  )
}

/**
 * The card grid.
 *
 * The mockup asks for `repeat(auto-fit, minmax(min(520px, 100%), 1fr))`. 520px
 * is not in `design/tokens.css` and a bracketed literal is a lint failure by
 * design, so the column count is expressed in breakpoints instead: one column
 * up to `xl`, two above it. At both captured widths that is the same layout the
 * export draws. Q-041 asks for the token that would let this be exact.
 */
function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 items-start gap-mdx-20 xl:grid-cols-2">{children}</div>
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-mdx-20 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-24 shadow-mdx-card">
      {children}
    </div>
  )
}

function Card({
  funnel,
  onDuplicate,
  onSaveAsBlueprint,
  onGetEmbedCode,
  onUnpublish,
  onArchive,
}: {
  funnel: FunnelCard
  onDuplicate: (funnel: FunnelCard) => void
  onSaveAsBlueprint: (funnel: FunnelCard) => void
  onGetEmbedCode: (funnel: FunnelCard) => void
  /** Opens the unpublish confirmation. Shown only on a published funnel. */
  onUnpublish: (funnel: FunnelCard) => void
  /** Opens the confirmation. Archive and Unpublish are the two that ask first. */
  onArchive: (funnel: FunnelCard) => void
}) {
  const live = funnel.status === 'published'
  const held = funnel.moderation?.state === 'held' ? funnel.moderation : null
  const blocked = funnel.moderation?.state === 'blocked' ? funnel.moderation : null
  /**
   * A blocked funnel with nothing live reads **Can't publish**, in the Draft
   * chip's neutral treatment, as the specimen draws it. One whose earlier version
   * is live keeps **Published** — it is — and says the rest below.
   */
  const status = blocked && !live ? { label: DRAWN.cardBlockedChip, tone: 'neutral' as const } : STATUS[funnel.status]
  const total = TONES.reduce((sum, tone) => sum + funnel.bands[tone], 0) + funnel.overCap

  return (
    <Shell>
      <div className="flex flex-wrap items-start justify-between gap-mdx-12">
        <span className="flex min-w-0 flex-1 basis-mdx-row-identity flex-col">
          {/*
            Story 6.2's editor, and there IS somewhere to land — `routes.tsx`
            declares `/funnels/:fid` and has since that story shipped. This read
            "not a link until there is somewhere to land" for long enough that
            the sentence stopped being true without anyone re-reading it.

            The mockup has always drawn it as one: `funnels-list.html` renders
            `<a href="#funnel">` at the same 17px/600/-0.018em ink, underline
            off, hovering to `rgb(19, 85, 180)` — which is `--mdx-brand-deep`,
            whose own token comment reads "hover, link text". So nothing here is
            a new visual decision; the styling is the span's, unchanged.

            A plain anchor rather than a click handler, because the shell resolves
            the route on load and `Funnels.tsx` already navigates this way to
            `/billing`. A link is also the thing you can middle-click, copy, and
            reach with a keyboard, none of which a span offers.

            fixes: the only signposted way back into a funnel's editor was the
            card menu's "Get embed code", which lands on the Connect tab — the
            obvious affordance was inert and the working one was mislabelled.
            Found on production while verifying an unrelated fix, 2026-09-09.
          */}
          <a
            href={`/funnels/${funnel.id}`}
            className="text-mdx-17 font-mdx-semibold tracking-mdx-h3 text-mdx-ink no-underline hover:text-mdx-brand-deep"
          >
            {funnel.name}
          </a>
          {/*
            A CHIP, not the 13px slate text the export drew. DECISION-065 part 2
            reversed the shipped build deliberately — "here the criterion wins
            over the export" — and then made the change flow through the mockups
            rather than around them, which is why this waited for DECISION-070's
            pass. `funnels-list.html` now draws it: 12px 600 on
            `--mdx-ground`, self-start so it hugs the name rather than
            stretching to the column.
          */}
          <span className="mt-mdx-4 self-start rounded-mdx-pill bg-mdx-ground px-mdx-10 py-mdx-4 text-mdx-12 font-mdx-semibold text-mdx-slate">
            {ARCHETYPE[funnel.archetype] ?? funnel.archetype}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-mdx-10">
          <Pill tone={status.tone} dot>
            {status.label}
          </Pill>
          <Menu
            label={`More actions for ${funnel.name}`}
            trigger={<span aria-hidden="true">⋯</span>}
            triggerClassName={TRIGGER}
          >
            {/* ALL FIVE, in the export's order — DECISION-063 closed on this
                menu by Story 6.8. Duplicate, Unpublish and Archive are Story
                6.6's services, Get embed code is Story 6.7's surface, Save as
                blueprint is Story 7.5's. `MenuItem` REQUIRES an `onClick`, so a
                drawn-and-dead item cannot be added here by forgetting one. */}
            <MenuItem onClick={() => onDuplicate(funnel)}>Duplicate</MenuItem>
            <MenuItem onClick={() => onSaveAsBlueprint(funnel)}>Save as blueprint</MenuItem>
            <MenuItem onClick={() => onGetEmbedCode(funnel)}>Get embed code</MenuItem>
            {/*
              ONLY ON A PUBLISHED FUNNEL, and NOT gated on `canPublish`
              (DECISION-122, Story 16.3). `lib/route-contract.ts` declares
              `unpublish` as `ws('editor')` and `publish` as
              `ws('editor', 'can_publish')` — stopping is Editor-level, starting
              is permission-gated, the same split archive and duplicate sit on.
              Making this symmetric with 16.1's Publish button is the expected
              mistake: it would disable itself for exactly the people the server
              allows, the mirror of the defect DECISION-120 refused on the
              publish side. `tests/unpublish-control.test.ts` holds it.

              No `divider` — the export gives `border-top` to Archive alone, and
              this item sits above that line rather than starting a group.
            */}
            {funnel.status === 'published' ? (
              <MenuItem onClick={() => onUnpublish(funnel)}>Unpublish</MenuItem>
            ) : null}
            <MenuItem divider onClick={() => onArchive(funnel)}>
              Archive
            </MenuItem>
          </Menu>
        </span>
      </div>

      {held ? <HeldNote live={live} /> : null}

      <div className="flex flex-wrap items-baseline gap-x-mdx-16 gap-y-mdx-8">
        <span className={`text-mdx-28 font-mdx-bold leading-mdx-tight tabular-nums`}>
          {funnel.submissions30d.toLocaleString('en-GB')}
        </span>
        <span className="text-mdx-15 text-mdx-slate">submissions in the last 30 days</span>
      </div>

      {/*
        THE SPECIMEN DRAWS THE BLOCKED CARD WITH NO BAND SPLIT: its explanation
        stands where the split would be, because a funnel that cannot publish has
        nothing to split and "Publish it and the band split fills in" would tell
        the person to do the one thing they cannot. A funnel whose earlier version
        is live still has its figures, so it keeps them and says the rest after.
      */}
      {blocked && !live ? null : <Split bands={funnel.bands} overCap={funnel.overCap} total={total} />}
      {blocked ? <BlockedNote funnelId={funnel.id} live={live} verticals={blocked.verticals} /> : null}

      <span className="border-t border-mdx-line-soft pt-mdx-14 text-mdx-13 text-mdx-slate">
        Edited {relative(funnel.updatedAt)}
        {funnel.updatedByName ? ` by ${funnel.updatedByName}` : ''}
      </span>
    </Shell>
  )
}

/**
 * Held for review — `funnels-list.html`'s `moderation` state, Story 25.4.
 *
 * The review tint and ink with the "~" square, under the card's header. A
 * funnel with nothing live is "waiting for review before it can publish"; one
 * whose earlier version is live is not waiting to publish at all, so the drawing
 * says its latest changes are, and that the live version keeps taking
 * submissions — which is what a held REPUBLISH does (Story 25.3).
 */
function HeldNote({ live }: { live: boolean }) {
  return (
    <div className="flex flex-wrap items-center gap-x-mdx-16 gap-y-mdx-12 rounded-mdx-control bg-mdx-review-tint px-mdx-16 py-mdx-12">
      <span
        aria-hidden="true"
        className="flex size-mdx-evidence-icon shrink-0 items-center justify-center rounded-mdx-control-sm bg-mdx-review text-mdx-14 font-mdx-bold text-mdx-white"
      >
        ~
      </span>
      <span className="min-w-0 flex-1 basis-mdx-row-identity text-mdx-15 font-mdx-medium text-mdx-review-ink">
        {live ? DRAWN.cardHeldLive : DRAWN.cardHeld}
      </span>
    </div>
  )
}

/**
 * Blocked on this plan — the specimen card, Story 25.4. Why, in the funnel's
 * own terms, and the two ways on: a plan that allows it, or the editor, whose
 * `publish-blocked` dialog says the rest.
 */
function BlockedNote({ funnelId, live, verticals }: { funnelId: string; live: boolean; verticals: Vertical[] }) {
  return (
    <div className="flex flex-col gap-mdx-10">
      <span className="max-w-mdx-measure-56 text-mdx-15 text-mdx-slate">
        {live ? COMPOSED.cardBlockedLive(verticals) : DRAWN.cardBlocked(verticals)}
      </span>
      <span className="flex flex-wrap items-center gap-mdx-8 text-mdx-15">
        <a href="/billing" className="font-mdx-semibold text-mdx-brand-deep">
          {DRAWN.seePlans}
        </a>
        <span aria-hidden="true" className="text-mdx-slate">
          ·
        </span>
        <a href={`/funnels/${funnelId}`} className="font-mdx-semibold text-mdx-brand-deep">
          {DRAWN.openTheFunnel}
        </a>
      </span>
    </div>
  )
}

/**
 * The band split — a bar and a legend, or the line that says there is nothing
 * to split yet.
 *
 * The percentages are DATA, not tokens, the same way `SeatBar`'s are.
 *
 * FOUR SEGMENTS, NOT THREE — DECISION-061. An over-cap lead is a submission and
 * carries a band nobody may see (DECISION-015, DECISION-060), so it is counted
 * and not banded. Counting it into `total` and drawing nothing would leave
 * three tones that quietly stop summing; excluding it would empty the split for
 * exactly the workspaces being upsold, since past the cap EVERY subsequent lead
 * is over it. The locked segment turns the arithmetic hole into the reason for
 * it. No band is published for any over-cap lead here or anywhere.
 */
function Split({
  bands,
  overCap,
  total,
}: {
  bands: Record<Tone, number>
  overCap: number
  total: number
}) {
  if (total === 0) {
    return (
      <div className="flex flex-col gap-mdx-8">
        <span className="block h-mdx-10 rounded-mdx-sm bg-mdx-line-soft" />
        <span className="text-mdx-13 text-mdx-slate">
          Nothing to show yet. Publish it and the band split fills in.
        </span>
      </div>
    )
  }

  const percent = (n: number) => Math.round((n / total) * 100)
  const count = (n: number) => n.toLocaleString('en-GB')

  /**
   * The label changes SHAPE when there is a locked segment, because the
   * segment needs explaining rather than measuring — a screen reader hearing
   * "11 percent" learns nothing about why that slice has no name. Both forms
   * are the mockup's own, one per card.
   */
  const label = overCap
    ? `${count(total)} submissions: ${TONES.map((t) => `${count(bands[t])} ${t}`).join(', ')}, ` +
      `and ${count(overCap)} scored but not banded because they arrived after this month's cap.`
    : `Band split: ${TONES.map((t) => `${t} ${percent(bands[t])} percent`).join(', ')}.`

  return (
    <div className="flex flex-col gap-mdx-8">
      <span role="img" aria-label={label} className="flex h-mdx-10 gap-mdx-2 overflow-hidden rounded-mdx-sm">
        {TONES.map((tone) =>
          bands[tone] > 0 ? (
            <span key={tone} className={FILL[tone]} style={{ width: `${percent(bands[tone])}%` }} />
          ) : null,
        )}
        {overCap > 0 ? (
          // `--mdx-line`, the only neutral in the palette that is not one of
          // the three tones. RULE-002 fixes grey → amber → green as the ladder,
          // so a fourth colour with any warmth in it would read as a fourth
          // band; this one reads as an absence, which is what it is.
          <span className="bg-mdx-line" style={{ width: `${percent(overCap)}%` }} />
        ) : null}
      </span>
      <span
        className={`flex flex-wrap gap-mdx-12 text-mdx-13 text-mdx-slate tabular-nums`}
        aria-hidden="true"
      >
        {TONES.map((tone) => (
          <span key={tone} className="inline-flex items-center gap-mdx-6">
            {/* The mockup's 8px swatch at radius 2px — `--mdx-radius-2`,
                added by DECISION-064, renamed by DECISION-072. */}
            <span className={`size-mdx-8 rounded-mdx-2 ${FILL[tone]}`} />
            {count(bands[tone])} {tone}
          </span>
        ))}
        {overCap > 0 ? (
          <span className="inline-flex items-center gap-mdx-6">
            {/* A padlock where the other three have a swatch — the legend entry
                for a bucket that has no colour because it has no band. */}
            <LockMark size="sm" />
            {count(overCap)} locked
          </span>
        ) : null}
      </span>

      {overCap > 0 ? <OverCapNote count={overCap} /> : null}
    </div>
  )
}

/**
 * What the locked segment is, and the way out of it.
 *
 * DECISION-061 calls the segment "a legible upsell affordance" — the operator
 * sees that leads exist and that their bands are gated, rather than seeing
 * three tones that stop summing. The sentence is what makes it legible; the
 * link is what makes it an affordance. Neither publishes a band.
 */
function OverCapNote({ count }: { count: number }) {
  return (
    <span className="flex flex-wrap items-center gap-x-mdx-12 gap-y-mdx-8 rounded-mdx-control bg-mdx-brand-tint px-mdx-12 py-mdx-10 text-mdx-brand-deep">
      <LockMark />
      {/*
        `--mdx-filter-column` is 220px, which is the basis the export gives this
        line. Borrowed for its measurement, not its name — the same way the card
        title above borrows `--mdx-row-identity`. Without a basis the row never
        wraps and the link crushes against the text at 375px.
      */}
      <span className="min-w-0 flex-1 basis-mdx-filter-column text-mdx-13">
        {count.toLocaleString('en-GB')} {count === 1 ? 'lead was' : 'leads were'} scored after this
        month&apos;s cap. Upgrade to see which band each one landed in.
      </span>
      <a
        href="/billing"
        className="whitespace-nowrap text-mdx-13 font-mdx-semibold text-mdx-brand-deep no-underline"
      >
        See plans
      </a>
    </span>
  )
}

/**
 * A menu row.
 *
 * `onClick` IS REQUIRED, and that is Story 6.8's real deliverable rather than a
 * style choice. The prop used to be optional and absence meant DECISION-063's
 * drawn-and-disabled — which is how three of these four items stayed dead
 * through two phases while their services sat built one directory away. Nobody
 * removed a wire; a story simply ended without adding one, and an optional prop
 * is what let that compile. `tests/nav-inventory.test.ts` asserts the prop is
 * still required, so re-opening the hole has to be deliberate.
 *
 * NOT `shell/Menu.tsx`'s `MenuButton`. This one carries the divider above
 * Archive that `funnels-list.html` draws, and that export colours Archive the
 * same ink as the other three — `MenuButton`'s `destructive` red is
 * `settings-workspaces.html`'s answer to the same question, not this screen's.
 * It does borrow `MenuButton`'s `role`, which this diverged from and should
 * not have: `Menu` renders `role="menu"`, and a menu whose children are plain
 * buttons is announced as a menu with no items in it.
 */
function MenuItem({
  divider = false,
  onClick,
  children,
}: {
  divider?: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      role="menuitem"
      type="button"
      onClick={onClick}
      className={[
        'cursor-pointer rounded-mdx-control-sm px-mdx-10 py-mdx-9 text-left text-mdx-15 text-mdx-ink',
        'transition-colors duration-mdx-swift ease-mdx-swift hover:bg-mdx-ground',
        divider ? 'mt-mdx-2 border-t border-mdx-line-soft pt-mdx-11' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </button>
  )
}

/**
 * The unpublish confirmation — the light stop, and the thing Archive is not.
 *
 * IT NAMES THE CONSEQUENCE BEFORE THE PRESS, for the reason `ArchiveModal`
 * does: unpublishing clears the KV pointer, so pressing this reaches out to a
 * page on somebody else's site. PRD §4.5's "never break a live embed on a
 * client's site" is why that is said rather than discovered.
 *
 * AND IT SAYS THE THING ARCHIVE'S MODAL CANNOT — the funnel stays in the list
 * and can be published again. That sentence is the whole point of the story:
 * Q-087 surfaced while writing 16.1's publish confirmation, which WANTED to say
 * "you can take it down again" and could not, because it was true of the server
 * and false of the product. It is true of both now.
 *
 * No published/draft branch, because the item is only drawn on a published
 * funnel — there is no second case for this modal to state.
 */
function UnpublishModal({
  funnel,
  busy,
  error,
  onClose,
  onConfirm,
}: {
  funnel: FunnelCard
  busy: boolean
  error: string | null
  onClose: () => void
  onConfirm: () => void
}) {
  return (
    <Modal
      title={`Unpublish ${funnel.name}?`}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Leave it live
          </Button>
          <Button loading={busy ? 'Unpublishing' : undefined} onClick={onConfirm}>
            Unpublish funnel
          </Button>
        </>
      }
    >
      <p className="m-0 max-w-mdx-measure-52 text-mdx-15 leading-mdx-body text-mdx-slate">
        <span className="font-mdx-semibold text-mdx-ink">It stops answering straight away.</span>{' '}
        The form stops taking submissions on every site the embed code is pasted on.
      </p>
      <p className="m-0 max-w-mdx-measure-52 text-mdx-13 text-mdx-slate">
        It stays in this list and you can publish it again whenever you like. Nothing is deleted,
        and the leads it has already collected are kept.
      </p>
      {error ? (
        <p role="alert" className="m-0 text-mdx-13 text-mdx-error">
          {error}
        </p>
      ) : null}
    </Modal>
  )
}

/**
 * The archive confirmation — the heavy stop, and the only one with no way back.
 *
 * TWO FACTS THE CARD CANNOT SHOW, which is the whole reason this is a modal:
 *
 * 1. A PUBLISHED funnel is answering somewhere right now. `archiveFunnel`
 *    clears the KV pointer, and its own comment is "an archived funnel must not
 *    keep answering" — so pressing this reaches out to a page on somebody
 *    else's site. PRD §4.5's "never break a live embed on a client's site" is
 *    why it has to be said rather than discovered. A draft is told the
 *    opposite, because telling a draft it has a live embed would be false.
 * 2. THERE IS NO WAY BACK. `services/funnels/publish.ts` says it plainly —
 *    "there is no unarchive route in the MVP either" — so this does not offer
 *    the restore `settings-workspaces.html`'s archive modal can offer, and
 *    names the thing that IS available instead.
 *
 * The shape is Story 4.2's: lead with what is kept, then the consequence, then
 * the small print. The leads really are kept and really do stay readable —
 * `leadPage` filters on the workspace and nothing else, so archiving a funnel
 * takes nothing off the leads list.
 *
 * STORY 16.3 RE-READ THIS COPY AND CHANGED ONE SENTENCE. Its AC asked whether
 * the modal had become misleading now that it is no longer the only way to stop
 * a published funnel; it had, by omission and in the direction that matters.
 * "It is published, so this takes it down" is still true, but it framed the
 * IRREVERSIBLE action as the take-down while a reversible one now sits one item
 * up the same menu, and the only hedge offered was "duplicate it first". So the
 * published branch names Unpublish by its menu label. The rest stands: the
 * consequence sentence and the no-way-back sentence were accurate before and
 * are accurate now.
 */
function ArchiveModal({
  funnel,
  busy,
  error,
  onClose,
  onConfirm,
}: {
  funnel: FunnelCard
  busy: boolean
  error: string | null
  onClose: () => void
  onConfirm: () => void
}) {
  return (
    <Modal
      title={`Archive ${funnel.name}?`}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Keep it active
          </Button>
          <Button loading={busy ? 'Archiving' : undefined} onClick={onConfirm}>
            Archive funnel
          </Button>
        </>
      }
    >
      <p className="m-0 max-w-mdx-measure-52 text-mdx-15 leading-mdx-body text-mdx-slate">
        {funnel.status === 'published' ? (
          <>
            <span className="font-mdx-semibold text-mdx-ink">
              It is published, so this takes it down.
            </span>{' '}
            The form stops answering on every site it is embedded on.
          </>
        ) : (
          <>
            It is not published, so no live embed changes. It leaves this list, and it can no
            longer be published.
          </>
        )}{' '}
        The leads it has already collected are kept — you can still read and export them.
      </p>
      <p className="m-0 max-w-mdx-measure-52 text-mdx-13 text-mdx-slate">
        Nothing is deleted. There is no way to bring an archived funnel back in Leadgible, so
        duplicate it first if you might want it again.
        {funnel.status === 'published'
          ? ' If you only want it to stop taking submissions, Unpublish does that and can be undone.'
          : ''}
      </p>
      {error ? (
        <p role="alert" className="m-0 text-mdx-13 text-mdx-error">
          {error}
        </p>
      ) : null}
    </Modal>
  )
}

/** The empty and error cards — one box, two tones, per the export. */
function StateCard({
  tone,
  title,
  body,
  action,
}: {
  tone: 'empty' | 'error'
  title: string
  body: string
  action: React.ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-mdx-12 rounded-mdx-card border border-mdx-line bg-mdx-white px-mdx-28 py-mdx-48 text-center shadow-mdx-card">
      <span
        aria-hidden="true"
        className={`size-mdx-40 rounded-mdx-control ${tone === 'error' ? 'bg-mdx-error' : 'bg-mdx-line'}`}
      />
      <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">{title}</span>
      <span className="max-w-mdx-measure-44 text-pretty text-mdx-15 text-mdx-slate">{body}</span>
      <span className="mt-mdx-4 flex flex-wrap justify-center gap-mdx-12">{action}</span>
    </div>
  )
}

/**
 * Four skeleton cards at the real card geometry.
 *
 * `DESIGN-SYSTEM.md` §2: "Never a bare centred spinner on a full page; preserve
 * the real layout so nothing jumps when content lands." The widths are the
 * export's own, which is why they are uneven.
 */
const SKELETONS = [
  { key: 'a', title: 56, sub: 30, count: 38, foot: 44 },
  { key: 'b', title: 64, sub: 26, count: 32, foot: 40 },
  { key: 'c', title: 48, sub: 24, count: 22, foot: 46 },
  { key: 'd', title: 42, sub: 20, count: 44, foot: 50 },
] as const

function LoadingGrid() {
  return (
    <Grid>
      {SKELETONS.map((s) => (
        <div
          key={s.key}
          className="flex flex-col gap-mdx-20 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-24"
        >
          <span className="flex justify-between gap-mdx-12">
            <span className="flex flex-1 flex-col gap-mdx-8">
              {/* A percentage IS data here in the same sense the band bar's is —
                  it is the export's own rhythm, not a design token. */}
              <Skeleton className="h-mdx-18 rounded-mdx-sm" style={{ width: `${s.title}%` }} />
              <Skeleton className="h-mdx-14 rounded-mdx-sm" style={{ width: `${s.sub}%` }} />
            </span>
            <Skeleton className="h-mdx-28 w-mdx-meta-column shrink-0 rounded-mdx-pill" />
          </span>
          <Skeleton className="h-mdx-28 rounded-mdx-control-sm" style={{ width: `${s.count}%` }} />
          <Skeleton className="h-mdx-10 rounded-mdx-sm" />
          <Skeleton className="h-mdx-14 rounded-mdx-sm" style={{ width: `${s.foot}%` }} />
        </div>
      ))}
    </Grid>
  )
}
